import { useQuery } from "react-query";
import { useRouter } from "next/router";
import { useSetAtom } from "jotai";
import dayjs from "dayjs";

import {
  getObituaries,
  getObituariesSangjuVersion,
  getObituariesVisitorVersion,
  ObituarySangjuVersionResponse,
  ObituaryVisitorVersionResponse,
} from "@/apis/obituaries";
import {
  DEFAULT_OBITUARY_SANGJU,
  obituaryAtom,
  obituarySangjusAtom,
  portraitPhotoAtom,
} from "../../atoms";
import { 사망_표현_LIST } from "../../constants";
import { ObituaryResponse } from "../../type";
import { getCdnImageSrc } from "goi_common";
import { isJejuAddress } from "@/utils/isJejuAddress";

export const useGetObituary = (obituaryKeyInMybugo?: string) => {
  const setObituary = useSetAtom(obituaryAtom);
  const setObituarySangjus = useSetAtom(obituarySangjusAtom);
  const setPortraitPhoto = useSetAtom(portraitPhotoAtom);

  const router = useRouter();
  const {
    obituaryKey,
    key: authenticatedKey,
    sangjuKey,
    temporary_key: temporaryKey,
  } = router.query as {
    obituaryKey?: string;
    key?: string;
    sangjuKey?: string;
    temporary_key?: string;
  };

  const queryKey =
    obituaryKeyInMybugo || obituaryKey || authenticatedKey || sangjuKey;
  const type =
    obituaryKeyInMybugo || obituaryKey
      ? "writer"
      : sangjuKey
      ? "visitor"
      : "sangju";

  return useQuery({
    // 임시 키가 다르면 응답도 달라지므로 캐시 분리
    queryKey: ["obituaries", queryKey, type, temporaryKey],
    queryFn: async () => {
      if (type === "visitor") {
        return await getObituariesVisitorVersion(queryKey || "", temporaryKey);
      }
      return await createObituaryAdapter(type).getObituary(queryKey || "");
    },
    select: (responseData) => {
      if (type === "writer") {
        const obituary = responseData as ObituaryResponse;
        return {
          ...obituary,
          // visitor 분기와 union 타입 호환을 위해 writer/sangju에서도 자리 유지.
          // 백엔드가 writer 응답에는 내려주지 않으므로 undefined로 명시.
          is_expired: undefined as boolean | undefined,
          temporary_access_expires_at: undefined as string | null | undefined,
          bank_account: null,
          bank_account_share_mode: undefined,
          bank_accounts: null,
          account_display_mode: undefined,
          sangjuInfo: null,
        };
      }

      if (type === "visitor") {
        const data = responseData as ObituaryVisitorVersionResponse;

        return {
          ...data?.obituary,
          // 만료(is_expired=true) 응답에서 백엔드가 obituary 데이터를 차단하면
          // sangjus가 undefined로 와서 onSuccess의 sangjus.length가 터진다.
          // visitor/sangju 양쪽 모두 빈 배열로 default.
          sangjus: data?.obituary?.sangjus ?? [],
          // top-level 신규 필드 보존 (BugoDetailContainer 게이트의 단일 신호)
          is_expired: data?.is_expired,
          temporary_access_expires_at: data?.temporary_access_expires_at,
          bank_account: null,
          bank_account_share_mode: undefined,
          bank_accounts: data?.bank_accounts,
          account_display_mode: data?.account_display_mode,
          sangjuInfo: {
            key: data.key,
            name: data.name,
            relation: data.relation,
            phone: data.phone,
          },
        };
      } else {
        const data = responseData as ObituarySangjuVersionResponse;

        return {
          ...data?.obituary,
          sangjus: data?.obituary?.sangjus ?? [],
          // sangju version 응답에 is_expired 포함 여부는 백엔드 wiki 미명시(B6).
          // 자리만 유지하고 실제 값은 백엔드 확인 후 채움.
          is_expired: undefined as boolean | undefined,
          temporary_access_expires_at: undefined as string | null | undefined,
          bank_account: data?.bank_account,
          bank_account_share_mode: data?.bank_account_share_mode,
          bank_accounts: null,
          account_display_mode: undefined,
          sangjuInfo: {
            key: data.key,
            name: data.name,
            relation: data.relation,
            phone: data.phone,
          },
        };
      }
    },
    onSuccess(responseData) {
      const obituary = {
        name: responseData?.name,
        age: responseData?.age,
        die_term: responseData?.die_term,
        binso: responseData?.binso,
        jangji: responseData?.jangji,
        parlor: responseData?.parlor?.id,
        passed_away_on: responseData?.passed_away_on,
        enter_parlor_at: responseData?.enter_parlor_at,
        enter_coffin_at: responseData?.enter_coffin_at,
        leave_parlor_at: responseData?.leave_parlor_at,
        sangjus: responseData?.sangjus,
        custom_message: responseData?.custom_message,
      };

      setObituary({
        name: obituary?.name || undefined,
        age: obituary?.age || undefined,
        // 서버 응답에서 null·undefined는 default true로 hydrate (서버 default와 일치)
        is_age_public: responseData?.is_age_public ?? true,
        die_term: obituary.die_term || 사망_표현_LIST[0],
        religion: responseData?.religion ?? undefined,
        religious_position: responseData?.religious_position ?? undefined,
        baptismal_name: responseData?.baptismal_name ?? undefined,
        binso: obituary?.binso || undefined,
        jangji: obituary?.jangji || undefined,
        parlor: obituary?.parlor || undefined,
        parlor_address: responseData?.parlor?.address,
        passed_away_on: obituary?.passed_away_on ?? undefined,
        enter_parlor_at: getDateAndTime(obituary?.enter_parlor_at ?? undefined),
        enter_coffin_at: getDateAndTime(obituary?.enter_coffin_at ?? undefined),
        leave_parlor_at: getDateAndTime(obituary?.leave_parlor_at ?? undefined),
        ilpo: responseData?.ilpo_at
          ? {
              date: dayjs(responseData.ilpo_at).format("YYYY-MM-DD"),
              time: dayjs(responseData.ilpo_at).format("HH:mm"),
            }
          : { date: responseData?.ilpo_on ?? undefined, time: undefined },
        // 제주면 서버 값 그대로 (null=옛 row만 표시로). 제주 아니면 서버가 false로 강제하므로
        // 표시로 시작: 나중에 제주로 바꾸고 날짜를 넣었는데 안 보이는 혼란 방지
        is_ilpo_public: isJejuAddress(responseData?.parlor?.address)
          ? responseData?.is_ilpo_public ?? true
          : true,
        custom_message: obituary?.custom_message ?? undefined,
      });

      if (responseData?.portrait) {
        setPortraitPhoto(
          getCdnImageSrc(`obituary_portrait/${responseData.portrait}`)
        );
      }

      if (obituary.sangjus.length > 0) {
        setObituarySangjus(() => [
          ...obituary.sangjus.map((sangju) => ({
            clientId: sangju.key as string,
            key: sangju.key as string,
            name: sangju.name,
            relation: sangju.relation,
            phone: {
              first: sangju.phone.slice(0, 3),
              second: sangju.phone.slice(4),
            },
            hasPhone: sangju.phone ? true : false,
          })),
        ]);
      } else {
        setObituarySangjus(() => [DEFAULT_OBITUARY_SANGJU]);
      }
    },
    retry: false,
  });
};

const getDateAndTime = (dateTimeString?: string) => {
  if (dateTimeString && dayjs(dateTimeString).isValid()) {
    return {
      date: dayjs(dateTimeString).format("YYYY-MM-DD"),
      time: dayjs(dateTimeString).format("HH:mm:ss"),
    };
  }

  return { date: undefined, time: undefined };
};

// 2. 어댑터 패턴
const createObituaryAdapter = (type: "writer" | "visitor" | "sangju") => {
  if (type === "writer") {
    return {
      getObituary: getObituaries,
    };
  }

  if (type === "visitor") {
    return {
      getObituary: getObituariesVisitorVersion,
    };
  } else {
    return {
      getObituary: getObituariesSangjuVersion,
    };
  }
};
